import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Checkbox, Spin, message, Tooltip } from "antd";
import {
  DeleteOutlined,
  UndoOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  getProjectTrashImages,
  restoreProjectImage,
  restoreProjectImagesBatch,
  restoreProjectVideo,
  restoreProjectVideosBatch,
  restoreProjectDocument,
} from "../../../services/projectServices";
import ProjectDocumentPreviewModal from "./ProjectDocumentPreviewModal";

export default function ProjectTrashModal({
  visible,
  onClose,
  projectId,
  onRestored,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [trashList, setTrashList] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [restoringKeys, setRestoringKeys] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);

  // Unique identifier preventing ID collisions between inspection_images and media tables
  const getItemKey = (item) => `${item.media_type || "photo"}_${item.id}`;

  const fetchTrash = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await dispatch(getProjectTrashImages(projectId));
      setTrashList(Array.isArray(data) ? data : []);
      setSelectedKeys([]);
    } catch (err) {
      console.error("Error fetching project trash media:", err);
      message.error("Failed to load trash items");
      setTrashList([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (visible && projectId) {
      fetchTrash();
    } else {
      setSelectedKeys([]);
      setRestoringKeys([]);
    }
  }, [visible, projectId, fetchTrash]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedKeys(trashList.map(getItemKey));
    } else {
      setSelectedKeys([]);
    }
  };

  const handleToggleSelect = (item) => {
    const key = getItemKey(item);
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSingleRestore = async (item) => {
    const key = getItemKey(item);
    setRestoringKeys((prev) => [...prev, key]);
    try {
      if (item.media_type === "document") {
        await dispatch(restoreProjectDocument(projectId, item.id));
        message.success("Document restored to project");
      } else if (item.media_type === "video") {
        await dispatch(restoreProjectVideo(item.id));
        message.success("Video restored to project");
      } else {
        await dispatch(restoreProjectImage(projectId, item.id));
        message.success("Photo restored to project gallery");
      }
      setTrashList((prev) => prev.filter((i) => getItemKey(i) !== key));
      setSelectedKeys((prev) => prev.filter((k) => k !== key));
      if (onRestored) onRestored();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error restoring item:", err);
      message.error(
        item.media_type === "document"
          ? "Failed to restore document"
          : item.media_type === "video"
          ? "Failed to restore video"
          : "Failed to restore photo"
      );
    } finally {
      setRestoringKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const handleBatchRestore = async () => {
    if (selectedKeys.length === 0) return;
    setRestoringKeys([...selectedKeys]);
    try {
      const selectedItems = trashList.filter((item) => selectedKeys.includes(getItemKey(item)));
      const docIds = selectedItems.filter((item) => item.media_type === "document").map((item) => item.id);
      const photoIds = selectedItems.filter((item) => item.media_type === "photo" || (!item.media_type && item.media_type !== "video" && item.media_type !== "document")).map((item) => item.id);
      const videoIds = selectedItems.filter((item) => item.media_type === "video").map((item) => item.id);

      const promises = [];
      if (docIds.length > 0) {
        docIds.forEach((id) => promises.push(dispatch(restoreProjectDocument(projectId, id))));
      }
      if (photoIds.length > 0) {
        promises.push(dispatch(restoreProjectImagesBatch(projectId, photoIds)));
      }
      if (videoIds.length > 0) {
        promises.push(dispatch(restoreProjectVideosBatch(videoIds)));
      }

      await Promise.all(promises);
      const totalCount = selectedKeys.length;
      message.success(`${totalCount} item${totalCount > 1 ? "s" : ""} restored successfully`);
      setTrashList((prev) => prev.filter((item) => !selectedKeys.includes(getItemKey(item))));
      setSelectedKeys([]);
      if (onRestored) onRestored();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error batch restoring items:", err);
      message.error("Failed to restore selected items");
    } finally {
      setRestoringKeys([]);
    }
  };

  const renderLuxuryBadge = (days) => {
    const d = days != null ? Number(days) : 30;
    let badgeClass = "trash-luxury-badge";

    if (d <= 5) {
      badgeClass += " urgent";
    } else if (d <= 14) {
      badgeClass += " warning";
    }

    return (
      <div className={badgeClass}>
        <ClockCircleOutlined className="badge-clock-icon" />
        <span className="badge-text">{d}d left</span>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1120}
      centered
      destroyOnClose
      style={{ top: 12, maxWidth: "calc(100vw - 16px)", margin: "0 auto" }}
      bodyStyle={{ padding: 0 }}
      className="project-trash-modal"
      title={
        <div className="trash-modal-header">
          <div className="trash-header-icon" style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "1px solid #a7f3d0", fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center" }}>
            ♻️
          </div>
          <div className="trash-header-text">
            <div className="trash-header-top-line">
              <span className="trash-header-title">Recycle Bin — Deleted Media</span>
              {trashList.length > 0 && (
                <span className="trash-header-count">
                  {trashList.length} {trashList.length === 1 ? "item" : "items"}
                </span>
              )}
            </div>
            <div className="trash-header-sub">
              Photos, videos & documents held for 30 days before permanent deletion. Restore anytime back to project.
            </div>
          </div>
        </div>
      }
    >
      <div style={{ padding: 0 }}>
        {/* Top Control Bar */}
        {trashList.length > 0 && (
          <div className="trash-toolbar">
            <div className="trash-toolbar-left">
              <Checkbox
                checked={
                  trashList.length > 0 && selectedKeys.length === trashList.length
                }
                indeterminate={
                  selectedKeys.length > 0 && selectedKeys.length < trashList.length
                }
                onChange={handleSelectAll}
              >
                <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
                  Select All
                </span>
              </Checkbox>

              {selectedKeys.length > 0 && (
                <span
                  className="trash-selection-chip"
                  onClick={() => setSelectedKeys([])}
                  title="Click to deselect all"
                >
                  <span>{selectedKeys.length} selected</span>
                  <CloseOutlined className="chip-close-icon" />
                </span>
              )}
            </div>

            <div className="trash-toolbar-right">
              <span className="trash-auto-hint">
                <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                Auto-deletes after 30 days
              </span>

              <Button
                type="primary"
                icon={<UndoOutlined />}
                disabled={selectedKeys.length === 0}
                loading={restoringKeys.length > 0 && selectedKeys.length > 0}
                onClick={handleBatchRestore}
                className="trash-batch-restore-btn"
              >
                Restore Selected {selectedKeys.length > 0 ? `(${selectedKeys.length})` : ""}
              </Button>
            </div>
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "70px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 14, color: "#64748b", fontSize: 13, fontWeight: 500 }}>
              Loading deleted media...
            </div>
          </div>
        ) : trashList.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "#f8fafc",
              borderRadius: 12,
              border: "1px dashed #cbd5e1",
              margin: "12px 0",
            }}
          >
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                color: "#16a34a",
                marginBottom: 12,
              }}
            >
              <CheckCircleOutlined />
            </div>
            <h4 style={{ margin: "0 0 6px 0", fontSize: 16, color: "#0f172a", fontWeight: 700 }}>
              Recycle Bin is Empty
            </h4>
            <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
              No photos, videos, or documents have been deleted from this project in the past 30 days.
            </p>
          </div>
        ) : (
          <div className="trash-grid-container">
            <div className="trash-grid">
              {trashList.map((item) => {
                const itemKey = getItemKey(item);
                const isSelected = selectedKeys.includes(itemKey);
                const isRestoring = restoringKeys.includes(itemKey);
                const isVideo = item.media_type === "video";
                const isDocument = item.media_type === "document";
                const imgThumb =
                  item.thumb_url || item.image_url || item.original_image_url;
                const formattedDate = item.deleted_at
                  ? new Date(item.deleted_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "Recently";
                const displayTitle =
                  item.name ||
                  item.description ||
                  item.original_filename ||
                  (isDocument ? `Document #${item.id}` : isVideo ? `Video #${item.id}` : `Photo #${item.id}`);

                return (
                  <div key={itemKey} className={`trash-card ${isSelected ? "selected" : ""}`}>
                    {/* Thumbnail with luxury badge & optional video overlay */}
                    <div
                      className="trash-thumb-wrapper"
                      onClick={() => handleToggleSelect(item)}
                    >
                      {isDocument ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                            padding: 12,
                            textAlign: "center",
                          }}
                        >
                          <FilePdfOutlined style={{ fontSize: 38, color: "#dc2626", marginBottom: 6 }} />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: "#dc2626",
                              letterSpacing: "0.5px",
                              background: "#ffffff",
                              padding: "2px 8px",
                              borderRadius: 4,
                              boxShadow: "0 1px 3px rgba(220, 38, 38, 0.15)",
                            }}
                          >
                            PDF DOCUMENT
                          </span>
                        </div>
                      ) : isVideo ? (
                        <video
                          src={`${item.source_url || imgThumb}#t=0.001`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                            display: "block",
                          }}
                          preload="metadata"
                          muted
                          playsInline
                        />
                      ) : imgThumb ? (
                        <img
                          src={imgThumb}
                          alt={item.original_filename || "Deleted photo"}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="trash-video-placeholder">
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>No Preview</span>
                        </div>
                      )}

                      {/* Video overlay icon & tag */}
                      {isVideo && (
                        <div className="trash-video-overlay">
                          <PlayCircleOutlined className="trash-video-play-icon" />
                          <span className="trash-video-tag">
                            <VideoCameraOutlined style={{ marginRight: 3 }} /> VIDEO
                          </span>
                        </div>
                      )}

                      {/* Checkbox top left */}
                      <div
                        className="trash-check-badge"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleSelect(item)}
                        />
                      </div>

                      {/* Luxury status badge top right */}
                      {renderLuxuryBadge(item.days_remaining)}
                    </div>

                    {/* Card Body */}
                    <div className="trash-card-info">
                      <Tooltip title={displayTitle}>
                        <div className="trash-file-title">
                          {displayTitle}
                        </div>
                      </Tooltip>

                      <div className="trash-file-meta">
                        <CalendarOutlined className="meta-calendar-icon" />
                        <span>Deleted: {formattedDate}</span>
                      </div>

                      {/* Card Action Buttons: View & Restore */}
                      <div className="trash-card-actions" style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        <Button
                          size="small"
                          className="trash-view-btn"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewItem(item);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          className="trash-restore-btn"
                          icon={<UndoOutlined />}
                          loading={isRestoring}
                          disabled={restoringKeys.length > 0}
                          onClick={() => handleSingleRestore(item)}
                        >
                          Restore
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal for Trashed Document */}
      {previewItem && previewItem.media_type === "document" && (
        <ProjectDocumentPreviewModal
          visible={Boolean(previewItem)}
          onClose={() => setPreviewItem(null)}
          document={previewItem}
        />
      )}

      {/* Preview Modal for Trashed Photo or Video */}
      {previewItem && previewItem.media_type !== "document" && (
        <Modal
          visible={Boolean(previewItem)}
          open={Boolean(previewItem)}
          onCancel={() => setPreviewItem(null)}
          footer={null}
          centered
          width={previewItem.media_type === "video" ? 820 : 720}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 32 }}>
              {previewItem.media_type === "video" ? (
                <VideoCameraOutlined style={{ color: "#0038d1", fontSize: 16 }} />
              ) : (
                <EyeOutlined style={{ color: "#0038d1", fontSize: 16 }} />
              )}
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {previewItem.description || previewItem.original_filename || (previewItem.media_type === "video" ? "Video Preview" : "Photo Preview")}
              </span>
            </div>
          }
          bodyStyle={{ padding: 0, background: "#0f172a", textAlign: "center" }}
        >
          {previewItem.media_type === "video" ? (
            <video
              src={previewItem.source_url || previewItem.thumb_url}
              controls
              autoPlay
              playsInline
              style={{ width: "100%", maxHeight: "75vh", display: "block", margin: "0 auto" }}
            />
          ) : (
            <img
              src={previewItem.original_image_url || previewItem.image_url || previewItem.thumb_url}
              alt="Trash Preview"
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block", margin: "0 auto" }}
            />
          )}
        </Modal>
      )}
    </Modal>
  );
}
